import {
  DragDropContext,
  DragStart,
  Droppable,
  DroppableStateSnapshot,
  DropResult,
} from '@hello-pangea/dnd';
import { useState } from 'react';

import {
  Modal,
  PageHeaderWithActions,
  RoutingSummary,
  Tabs,
} from 'src/components/molecules';
import { CancelModal } from 'src/components/molecules/CancelModal';
import {
  RoutingAuditLogTable,
  RoutingFallback,
  RoutingRuleAndPriority,
  SuggestedReading,
} from 'src/components/organisms';
import { RoutingVariant } from 'src/components/organisms/RoutingRuleAndPriority';
import { GroupType, useGroupsQuery } from 'src/graphql';
import { useIsFeatureEnabled, useIsLoading } from 'src/hooks';
import { FeatureFlag } from 'src/types';
import { routingIntercomArticles } from 'src/utils/intercomArticles';
import { Loading } from '../Loading';
import {
  useFetchRoutingRules,
  useMutateRoutingRules,
  useRoutingAttributes,
  useRoutingRuleCoordinates,
} from './hooks';
import { useLeaveRoutingPage } from './hooks/useLeaveRoutingPage';
import {
  GridContainer,
  PageContainer,
  PriorityInfo,
  PriorityParagraph,
  PriorityTitleContainer,
  RoutingBody,
  RoutingContainer,
  RoutingSubtitle,
  RoutingTabsWrapper,
  RulesContainer,
} from './Routing.styles';

export const Routing = () => {
  const [priorityModalOpen, setPriorityModalOpen] = useState(false);
  const [auditLogModalOpen, setAuditLogModalOpen] = useState(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [unsavedRulesExist, setUnsavedRulesExist] = useState<boolean>(false);
  const [allRulesAreSaved, setAllRulesAreSaved] = useState<boolean>(true);
  const [leavingPage, setLeavingPage] = useState<boolean>(false);
  const [saveRulesBeforeLeaving, setSaveRulesBeforeLeaving] =
    useState<boolean>(false);
  const [activeTab, setActiveTab] = useState(0);
  const isCoiEnabled = useIsFeatureEnabled(FeatureFlag.CONFLICT_OF_INTEREST);
  const isSpeakUpEnabled = useIsFeatureEnabled(FeatureFlag.SPEAK_UP);

  const isCOI =
    (activeTab === 1 && isCoiEnabled && isSpeakUpEnabled) ||
    (isCoiEnabled && !isSpeakUpEnabled);

  const { orderedRules, count, isRoutingRulesLoading, openIds, setOpenIds } =
    useFetchRoutingRules(isCOI);
  const { createNewRule, newRule, newRuleRef, updateRulePriorities } =
    useMutateRoutingRules({
      setOpenIds,
      isCOI,
    });
  const {
    rulesContainerRef,
    setRef,
    ruleJoinerCoordinates,
    recalculateCoordinates,
  } = useRoutingRuleCoordinates({ orderedRules, draggingId });
  const { attributes, isRoutingAttributesLoading } =
    useRoutingAttributes(isCOI);
  const { data: groups, isLoading: isGroupsLoading } = useGroupsQuery();

  const isLoading = useIsLoading([
    isRoutingRulesLoading,
    isRoutingAttributesLoading,
    isGroupsLoading,
  ]);

  const {
    onLeaveRoutingPage,
    onSave,
    onDismiss,
    showCancellationModal,
    setNextLocation,
    setShowCancellationModal,
  } = useLeaveRoutingPage({
    unsavedRulesExist,
    setUnsavedRulesExist,
    setSaveRulesBeforeLeaving,
    setLeavingPage,
    allRulesAreSaved,
  });

  const noRoutingRules = orderedRules.length === 0;

  const onDragStart = ({ draggableId }: DragStart) => {
    setDraggingId(draggableId);
  };

  const onDragEnd = (result: DropResult) => {
    setDraggingId(null);
    if (!result.destination) {
      return;
    }
    const items = [...orderedRules];
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    const newRules = items.map((item, index) => ({
      ...item,
      priority: count - index,
    }));

    updateRulePriorities({
      rules: newRules.map((item) => ({ id: item.id, priority: item.priority })),
    });
  };

  const [heightOfRuleBeingDragged, setHeightOfRuleBeingDragged] = useState(0);
  const ruleBeingDraggedIsOpen = (snapshot: DroppableStateSnapshot) =>
    !!snapshot.draggingOverWith && openIds.includes(snapshot.draggingOverWith);

  if (isLoading) {
    return <Loading />;
  }

  const makeRoutingBodyContent = (droppableId: string) => (
    <>
      {!noRoutingRules && (
        <PriorityTitleContainer>
          <RoutingSubtitle>PRIORITY</RoutingSubtitle>
          <PriorityInfo
            className="uil uil-info-circle"
            size="24px"
            onClick={() => setPriorityModalOpen(true)}
          />
        </PriorityTitleContainer>
      )}
      <GridContainer ref={rulesContainerRef}>
        <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <Droppable droppableId={droppableId}>
            {(provided, snapshot) => (
              <RulesContainer
                rows={orderedRules.length}
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {orderedRules?.map((rule, index) => (
                  <RoutingRuleAndPriority
                    key={rule.id}
                    rule={rule}
                    index={index}
                    count={count}
                    ruleJoinerCoordinates={ruleJoinerCoordinates}
                    newRule={newRule}
                    newRuleRef={newRuleRef}
                    setRef={setRef}
                    groups={groups?.orgGroups as GroupType[]}
                    attributes={attributes}
                    openRule={() =>
                      openIds.includes(rule.id)
                        ? setOpenIds(openIds.filter((id) => id !== rule.id))
                        : setOpenIds([...openIds, rule.id])
                    }
                    recalculateCoordinates={recalculateCoordinates}
                    isDragDisabled={openIds.includes(rule.id)}
                    defaultOpen={openIds.includes(rule.id)}
                    isDragging={rule.id === snapshot.draggingOverWith}
                    setHeightOfRuleBeingDragged={setHeightOfRuleBeingDragged}
                    saveRulesBeforeLeaving={saveRulesBeforeLeaving}
                    onLeavingRoutingRulePage={
                      leavingPage ? onLeaveRoutingPage : undefined
                    }
                    setAllRulesAreSaved={setAllRulesAreSaved}
                    variant={
                      isCoiEnabled && isCOI
                        ? RoutingVariant.COI
                        : RoutingVariant.SPEAK_UP
                    }
                  />
                ))}
                <RoutingFallback
                  groups={(groups?.orgGroups ?? []) as GroupType[]}
                  isDragging={snapshot.isDraggingOver}
                  variant={isCoiEnabled && isCOI ? 'coi' : 'speak-up'}
                  marginTop={
                    ruleBeingDraggedIsOpen(snapshot)
                      ? `${heightOfRuleBeingDragged}px`
                      : '100px'
                  }
                />
                {provided.placeholder}
              </RulesContainer>
            )}
          </Droppable>
        </DragDropContext>
        <div>
          <RoutingSummary orderedRules={orderedRules} />
          <SuggestedReading articles={routingIntercomArticles} />
        </div>
      </GridContainer>
    </>
  );

  return (
    <PageContainer data-testid="routing-page">
      <RoutingContainer>
        <PageHeaderWithActions
          createItem={createNewRule}
          openAuditLog={() => setAuditLogModalOpen(true)}
          createButtonText="New routing rule"
          title="Routing"
        />
        <RoutingBody>
          {isCoiEnabled && isSpeakUpEnabled && (
            <RoutingTabsWrapper>
              <Tabs
                headers={['Speak up', 'Conflict of interest']}
                activeTab={activeTab}
                onClick={setActiveTab}
                panels={[
                  activeTab === 0
                    ? makeRoutingBodyContent('rules-speak-up')
                    : null,
                  activeTab === 1 ? makeRoutingBodyContent('rules-coi') : null,
                ]}
              />
            </RoutingTabsWrapper>
          )}
          {(isSpeakUpEnabled || isCoiEnabled) &&
            !(isSpeakUpEnabled && isCoiEnabled) &&
            makeRoutingBodyContent('rules')}
        </RoutingBody>
      </RoutingContainer>
      <CancelModal
        size="medium"
        title="Unsaved routing rule changes"
        description={
          <div>
            <p>
              You have unsaved routing rule edits. Leaving without saving will
              result in loss of changes.
              <br />
              Please save now.
            </p>
            <ul>
              <li>Save changes: Click &quot;Save&quot; below.</li>
              <li>
                Dismiss changes: Click &quot;Dismiss&quot; to abort your edits.
              </li>
            </ul>
          </div>
        }
        showCancellationModal={showCancellationModal}
        preventNavigation={unsavedRulesExist}
        setNextLocation={setNextLocation}
        onConfirm={onSave}
        onCancel={onDismiss}
        setOpen={setShowCancellationModal}
        setShowCancellationModal={setShowCancellationModal}
        cancelButtonText="Dismiss"
        confirmButtonText="Save"
      />
      <Modal
        size="medium"
        title="Optimizing Event Routing: Mastering Rule Order and Priority"
        description={
          <>
            <PriorityParagraph>
              When multiple event routing rules exist, their order can be
              adjusted by dragging and dropping them into new positions. Events
              are evaluated based on the specified order of these rules until a
              match is found. It&apos;s crucial to arrange the rules
              thoughtfully, placing more specific and higher-priority rules at
              the start of the list.
            </PriorityParagraph>
            <PriorityParagraph>
              The event is processed by the first routing rule that matches its
              criteria. For instance, if an event qualifies for two routing
              rules, only the first one encountered is applied. Please note that
              the default routing rule remains fixed and always appears at the
              end, unable to be reordered.
            </PriorityParagraph>
          </>
        }
        open={priorityModalOpen}
        setOpen={setPriorityModalOpen}
        onConfirm={() => setPriorityModalOpen(false)}
        confirmButtonText="OK"
      />
      <RoutingAuditLogTable
        isOpen={auditLogModalOpen}
        setIsOpen={setAuditLogModalOpen}
      />
    </PageContainer>
  );
};
